import React, { ReactNode } from "react";
import styled, { keyframes, useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { AmountType, BasicBotData, BotStatus, BotType } from "@shared/types";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import {
  formatDateRelative,
  getFormattedTimeDuration,
} from "@shared/utils/date";
import {
  countDecimals,
  getLocaleStringWithPrecision,
  round,
  toFixed,
} from "@shared/utils/numbers";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { sharedLib } from "@shared/services/shared.service";
import { hexToRGB } from "@shared/utils/misc";
import { DOLLAR_COINS } from "@shared/common/constants";
import { skipToken } from "@reduxjs/toolkit/query";
import { DISPLAY_CURRENCIES } from "common/constants";
import BotChange from "features/bots/common/BotChange";
import NewLookBotTradeBar from "features/bots/NewLook/NewLookBotTradeBar";
import { NewLookGridRange } from "features/bots/NewLook/useNewLookGridRange";
import { withThousands } from "features/bots/NewLook/newLookBotUtils";
import {
  NowEnds,
  NowMeta,
  NowSection,
  NowState,
  NowTitle,
  NowTop,
  NowValue,
} from "features/bots/NewLook/newLookNowPanelStyles";
import { getNewLookTokens } from "common/NewLook/tokens.ts";

interface NewLookBotNowPanelProps {
  botData: BasicBotData;
  /** A bot with nothing to show yet (no closed and no open trades). */
  isNewBot: boolean;
  /** A grid bot's range and live price (see useNewLookGridRange). */
  gridRange?: NewLookGridRange;
}

/**
 * The bot card's "now" section, under the hairline that follows the bot's
 * result. No wells, no boxes: whitespace, one bold line and one muted meta
 * line.
 * - DCA: the active deal (header row, slim ladder, stop / take-profit, meta)
 *   or the idle state;
 * - signals / TradingView: the open trades with their unrealized PnL, or the
 *   idle state;
 * - grid: the range bar with the live price;
 * - hedging: the long / short exposure split;
 * - a new bot: "Waiting for the first signal / alert / deal".
 */
const NewLookBotNowPanel = ({
  botData,
  isNewBot,
  gridRange,
}: NewLookBotNowPanelProps) => {
  switch (botData.bot_type) {
    case BotType.SIGNAL:
    case BotType.TRADING_VIEW:
    case BotType.TRADING_VIEW_SIGNALS:
      return isNewBot ? (
        <NewBotPanel botData={botData} />
      ) : (
        <TradesPanel botData={botData} />
      );
    case BotType.ASAP:
      return <DealPanel botData={botData} />;
    case BotType.GRID:
      return (
        <GridPanel botData={botData} range={gridRange} isNewBot={isNewBot} />
      );
    case BotType.HEDGING:
      return <HedgingPanel botData={botData} />;
    default:
      return null;
  }
};

export default NewLookBotNowPanel;

const useMoney = () => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();
  const symbol = displayCurrency === DISPLAY_CURRENCIES.USD ? "$" : "₿";
  return {
    displayCurrency,
    money: (value: number) =>
      `${value < 0 ? "−" : ""}${symbol}${getLocaleStringWithPrecision(
        Math.abs(value),
        precision
      )}`,
  };
};

/**
 * A price as text, at the pair's price precision: "$112,480.5" for dollar
 * pairs, "0.0562 BTC" otherwise. Without a known precision, the value's own
 * decimals (never scientific notation). `minDecimals` pads trailing zeros so
 * a range's two ends line up ($0.14 next to $0.20, not $0.2).
 */
const formatPrice = (
  value: number,
  symbol?: string,
  precision?: number,
  minDecimals = 0
) => {
  const quote = (symbol || "").split("/")[1];
  // Intl takes up to 20 fraction digits.
  const digits = Math.min(precision ?? countDecimals(value), 20);
  const text =
    minDecimals > 0
      ? round(value, digits).toLocaleString(undefined, {
          minimumFractionDigits: Math.min(minDecimals, digits),
          maximumFractionDigits: digits,
        })
      : getLocaleStringWithPrecision(value, digits, true);
  if (!quote) return text;
  return DOLLAR_COINS.includes(quote) ? `$${text}` : `${text} ${quote}`;
};

/** Shared decimals for a range's two ends (see formatPrice). */
const rangeDecimals = (range: NewLookGridRange) => {
  const decimalsOf = (value: number) =>
    countDecimals(round(value, range.pricePrecision ?? countDecimals(value)));
  return Math.max(decimalsOf(range.min), decimalsOf(range.max));
};

/** "6h" + "ago" -> "6h ago" (nothing without a time). */
const useAgo = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  return (time?: string) => (time ? `${time} ${t("common.ago")}` : "");
};

/** An idle state: one bold line (and the meta right under it). */
const IdleState = ({
  title,
  meta,
}: {
  title: ReactNode;
  meta?: ReactNode[];
}) => (
  <NowSection data-now-state="idle">
    <NowTop>
      <NowTitle>{title}</NowTitle>
    </NowTop>
    <NowMeta items={meta || []} gap={6} />
  </NowSection>
);

/** Signals / TradingView with history: open trades, or what it waits for. */
const TradesPanel = ({ botData }: { botData: BasicBotData }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const botType = botData.bot_type;
  const isStopped = botData.status !== BotStatus.ACTIVE;
  const openTrades = botData.number_of_open_trades || 0;
  const closedTrades = botData.number_of_closed_trades || 0;
  const lastTrade = botData.last_trade
    ? formatDateRelative(botData.last_trade, true, true, true)
    : undefined;
  const isTradingView =
    botType === BotType.TRADING_VIEW ||
    botType === BotType.TRADING_VIEW_SIGNALS;
  // The card's headline already is the unrealized PnL (nothing closed yet).
  const leadsWithUnrealized =
    (botType === BotType.SIGNAL || botType === BotType.TRADING_VIEW) &&
    closedTrades === 0;
  const lastTradeText = lastTrade
    ? t("card_idle.last_trade", { time: lastTrade })
    : undefined;

  if (openTrades > 0) {
    return (
      <NowSection data-now-state="live">
        <NowTop>
          <NowTitle>
            {t(openTrades === 1 ? "card.open_trade" : "card.open_trades", {
              count: openTrades,
            })}
          </NowTitle>
          {!leadsWithUnrealized && (
            <NowValue>
              <BotChange botData={botData} isOpenTrades={true} />
            </NowValue>
          )}
        </NowTop>
        <NowMeta
          gap={6}
          items={
            leadsWithUnrealized
              ? [lastTradeText, t("card.results_when_closed")]
              : [t("card.unrealized_pnl"), lastTradeText]
          }
        />
      </NowSection>
    );
  }

  const title = isStopped
    ? t("card_idle.stopped")
    : isTradingView
    ? t("card_idle.waiting_for_alert")
    : t("card_idle.waiting_for_next_trade");
  return (
    <IdleState
      title={title}
      meta={[
        isTradingView && lastTrade
          ? t("card.last_alert", { time: lastTrade })
          : lastTradeText,
      ]}
    />
  );
};

/** DCA: the active deal, or what it waits for. */
const DealPanel = ({
  botData,
  isGrid,
}: {
  botData: BasicBotData;
  isGrid?: boolean;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const ago = useAgo();
  const { displayCurrency, money } = useMoney();
  const activeTrade = botData.active_trade;
  const symbol = botData.symbols?.[0];
  const isStopped = botData.status !== BotStatus.ACTIVE;

  if (!activeTrade) {
    const lastDeal =
      botData.last_trade &&
      formatDateRelative(botData.last_trade, true, true, true);
    if (isStopped || isGrid)
      return (
        <IdleState
          title={
            isStopped ? t("card_idle.stopped") : t("card.waiting_next_deal")
          }
          meta={[lastDeal && t("card.last_deal_opened", { time: lastDeal })]}
        />
      );
    // A running DCA bot between deals: the same calm waiting state as a new
    // grid (pulse, one line on what happens next, two facts).
    const investment = dcaInvestment(botData);
    const closed = botData.number_of_closed_trades || 0;
    return (
      <NowSection data-now-state="idle">
        <Waiting>
          <Pulse aria-hidden={true}>
            <i />
            <i />
            <i />
            <i />
          </Pulse>
          <div>
            <WaitTitle>{t("card.waiting_next_deal")}</WaitTitle>
            <WaitDescription>
              {symbol
                ? t("card.dca_waiting_desc", { symbol })
                : t("card.dca_waiting_desc_generic")}
            </WaitDescription>
          </div>
        </Waiting>
        <FactTiles $count={investment ? 2 : 1}>
          {investment && (
            <Fact label={t("card.fact_investment")} value={investment} />
          )}
          <Fact
            label={t("card.fact_last_deal")}
            value={lastDeal || (closed ? "—" : t("card.fact_no_deals_yet"))}
          />
        </FactTiles>
      </NowSection>
    );
  }

  const { entries_filled } = activeTrade;
  const committed = entries_filled?.amount?.[displayCurrency];
  const lastEntry = activeTrade.last_entry;

  return (
    <NowSection data-now-state="live">
      <NowTop>
        <NowTitle>
          {isGrid ? (
            t("labels.active_grid")
          ) : symbol ? (
            <>
              {t("card.active_deal_label")} <span>· {symbol}</span>
            </>
          ) : (
            t("labels.active_trade")
          )}
        </NowTitle>
        <NowValue>
          <BotChange botData={botData} isOpenTrades={true} />
        </NowValue>
      </NowTop>
      <LadderGap>
        <NewLookBotTradeBar
          trade={activeTrade}
          symbol={symbol}
          exchange={botData.exchange}
          botReverse={botData.reverse}
        />
      </LadderGap>
      <NowMeta
        pin={true}
        items={[
          !!entries_filled?.total &&
            `${t("card.entries")} ${entries_filled.number}/${
              entries_filled.total
            }`,
          !!committed &&
            t("card.committed_amount", { amount: money(committed) }),
          !!lastEntry?.time_passed &&
            t("card.last_order_ago", { time: ago(lastEntry.time_passed) }),
        ]}
      />
    </NowSection>
  );
};

/**
 * The grid's range bar. Live: buy orders live below the price (green), sell
 * orders above it (red); the price pins to the edge, in orange, when it leaves
 * the range; tick marks per grid level up to 12 grids. Dim: a dashed neutral
 * track and a grey marker (a grid that is not running). Without a live price
 * there is no marker (and no green / red split).
 */
const GridRangeBar = ({
  range,
  symbol,
  dim,
}: {
  range: NewLookGridRange;
  symbol?: string;
  dim?: boolean;
}) => {
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const isOut = range.state === "above" || range.state === "below";
  const split = range.position === undefined ? undefined : range.position * 100;
  const markerColor = dim
    ? tokens.muted
    : isOut
    ? theme.colors.orange
    : tokens.ink;
  // Keep the price pill inside the bar when the price is near an edge.
  const pillLeft =
    split === undefined
      ? 0
      : isOut
      ? range.state === "above"
        ? 92
        : 8
      : split;
  const span = range.max - range.min;
  const decimals = rangeDecimals(range);
  const showTicks = !dim && range.levels.length <= 12;
  const neutral = hexToRGB(tokens.muted, 0.28);
  const background = dim
    ? `repeating-linear-gradient(90deg, ${neutral} 0 6px, transparent 6px 10px)`
    : split === undefined
    ? neutral
    : `linear-gradient(to right, ${hexToRGB(
        tokens.up,
        0.45
      )} ${split}%, ${hexToRGB(tokens.down, 0.35)} ${split}%)`;
  return (
    <>
      <Bar
        style={{
          background,
          // No price pill above the bar: less room needed.
          marginTop: split === undefined ? 24 : undefined,
        }}
      >
        {showTicks &&
          range.levels.map((level) => (
            <Tick
              key={level}
              style={{ left: `${((level - range.min) / span) * 100}%` }}
            />
          ))}
        {range.price !== undefined && split !== undefined && (
          <>
            <Pin
              style={{
                left: `${isOut ? (range.state === "above" ? 100 : 0) : split}%`,
                background: markerColor,
              }}
            />
            <PricePill
              style={{ left: `${pillLeft}%`, background: markerColor }}
            >
              {formatPrice(range.price, symbol, range.pricePrecision)}
            </PricePill>
          </>
        )}
      </Bar>
      <NowEnds>
        <b>{formatPrice(range.min, symbol, range.pricePrecision, decimals)}</b>
        <b>{formatPrice(range.max, symbol, range.pricePrecision, decimals)}</b>
      </NowEnds>
    </>
  );
};

/** What a new grid commits: its fixed amount, else how wide its range is. */
/** A DCA bot's fixed amount per deal as text, when it has one. */
const dcaInvestment = (botData: BasicBotData) => {
  const { amount_type: type, amount_value: value } = botData;
  if (value && type === AmountType.FIXED_USD_AMOUNT)
    return `$${withThousands(String(value))}`;
  if (value && type === AmountType.FIXED_BTC_AMOUNT)
    return `${withThousands(String(value))} BTC`;
  return undefined;
};

const useGridFact = (botData: BasicBotData, range: NewLookGridRange) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { amount_type: type, amount_value: value } = botData;
  if (value && type === AmountType.FIXED_USD_AMOUNT) {
    return {
      label: t("card.fact_investment"),
      value: `$${withThousands(String(value))}`,
    };
  }
  if (value && type === AmountType.FIXED_BTC_AMOUNT) {
    return {
      label: t("card.fact_investment"),
      value: `${withThousands(String(value))} BTC`,
    };
  }
  const half = ((range.max - range.min) / (range.max + range.min)) * 100;
  return {
    label: t("card.fact_range_width"),
    value: `±${Math.round(half * 10) / 10}%`,
  };
};

/**
 * A grid that has not placed its orders yet: the waiting row, the configured
 * range (with the live price when there is one) and two fact tiles.
 */
const StartingGrid = ({
  botData,
  range,
}: {
  botData: BasicBotData;
  range?: NewLookGridRange;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const symbol = botData.symbols?.[0];
  const decimals = range ? rangeDecimals(range) : 0;
  return (
    <NowSection data-now-state="idle">
      <Waiting>
        <Pulse aria-hidden={true}>
          <i />
          <i />
          <i />
          <i />
        </Pulse>
        <div>
          <WaitTitle>{t("card.grid_starting")}</WaitTitle>
          <WaitDescription>
            {range
              ? t("card.grid_starting_desc", {
                  count: range.levels.length,
                  min: formatPrice(
                    range.min,
                    symbol,
                    range.pricePrecision,
                    decimals
                  ),
                  max: formatPrice(
                    range.max,
                    symbol,
                    range.pricePrecision,
                    decimals
                  ),
                })
              : t("card.grid_starting_desc_generic")}
          </WaitDescription>
        </div>
      </Waiting>
      {range && (
        <>
          <GridRangeBar range={range} symbol={symbol} />
          <StartingFacts botData={botData} range={range} />
        </>
      )}
    </NowSection>
  );
};

const StartingFacts = ({
  botData,
  range,
}: {
  botData: BasicBotData;
  range: NewLookGridRange;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const fact = useGridFact(botData, range);
  return (
    <FactTiles $count={2}>
      <Fact label={t("card.fact_grids")} value={range.levels.length} />
      <Fact label={fact.label} value={fact.value} />
    </FactTiles>
  );
};

/**
 * Grid: where the live price sits in the grid's range (see GridRangeBar). A
 * grid that is not running (no active trade, or stopped) keeps the range, dim,
 * with the price marker in grey.
 */
const GridPanel = ({
  botData,
  range,
  isNewBot,
}: {
  botData: BasicBotData;
  range?: NewLookGridRange;
  isNewBot: boolean;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const ago = useAgo();
  const symbol = botData.symbols?.[0];
  const isStopped = botData.status !== BotStatus.ACTIVE;

  if (isNewBot && !isStopped && !botData.active_trade) {
    return <StartingGrid botData={botData} range={range} />;
  }

  if (!range) {
    return botData.active_trade ? (
      <DealPanel botData={botData} isGrid={true} />
    ) : (
      <IdleState
        title={
          isStopped ? t("card_idle.stopped") : t("card.grid_waits_restart")
        }
      />
    );
  }

  const isRunning = !isStopped && !!botData.active_trade;
  const isOut = range.state === "above" || range.state === "below";
  const lastGrid = botData.last_grid_filled
    ? t("card.last_grid_filled", {
        time: ago(botData.last_grid_filled.time_passed),
      })
    : undefined;

  return (
    <NowSection data-now-state={isRunning ? "live" : "idle"}>
      <NowTop>
        <NowTitle>
          {t("card.grid_range")}{" "}
          <span>· {t("card.grids_count", { count: range.levels.length })}</span>
        </NowTitle>
        {!isRunning ? (
          <NowState>
            {isStopped ? t("card_idle.stopped") : t("card.grid_not_running")}
          </NowState>
        ) : isOut ? (
          <NowState $color={theme.colors.orange} $bold={true}>
            {range.state === "above"
              ? t("card.price_above_range")
              : t("card.price_below_range")}
          </NowState>
        ) : range.price !== undefined ? (
          <NowState>{t("card.inside_range")}</NowState>
        ) : null}
      </NowTop>
      <GridRangeBar range={range} symbol={symbol} dim={!isRunning} />
      <NowMeta
        pin={true}
        items={
          !isRunning
            ? [lastGrid || (!isStopped && t("card.grid_waits_restart"))]
            : isOut
            ? [t("card.no_orders_until_return")]
            : [t("card.buy_below_sell_above"), lastGrid]
        }
      />
    </NowSection>
  );
};

/** Hedging: the long / short split of the current exposure. */
const HedgingPanel = ({ botData }: { botData: BasicBotData }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const ago = useAgo();
  const exposure = botData.current_exposure;
  const isStopped = botData.status !== BotStatus.ACTIVE;
  const lastRebalance = botData.last_grid_filled
    ? t("card.last_rebalance", {
        time: ago(botData.last_grid_filled.time_passed),
      })
    : undefined;

  if (exposure === undefined || exposure === null) {
    if (isStopped)
      return (
        <IdleState title={t("card_idle.stopped")} meta={[lastRebalance]} />
      );
    // Running without an open hedge yet: the same waiting state as the other
    // new bots (pulse, what happens next, two facts).
    const symbol = botData.symbols?.[0];
    const investment = dcaInvestment(botData);
    return (
      <NowSection data-now-state="idle">
        <Waiting>
          <Pulse aria-hidden={true}>
            <i />
            <i />
            <i />
            <i />
          </Pulse>
          <div>
            <WaitTitle>{t("card.hedge_starting")}</WaitTitle>
            <WaitDescription>
              {symbol
                ? t("card.hedge_starting_desc", { symbol })
                : t("card.hedge_starting_desc_generic")}
            </WaitDescription>
          </div>
        </Waiting>
        <FactTiles $count={investment ? 2 : 1}>
          {investment && (
            <Fact label={t("card.fact_investment")} value={investment} />
          )}
          <Fact
            label={t("card.fact_last_rebalance")}
            value={
              botData.last_grid_filled
                ? ago(botData.last_grid_filled.time_passed)
                : t("card.fact_no_deals_yet")
            }
          />
        </FactTiles>
      </NowSection>
    );
  }
  const long = Math.min(Math.max(toFixed(exposure * 100, 2), 0), 100);
  const short = toFixed(100 - long, 2);
  return (
    <NowSection data-now-state="live">
      <NowTop>
        <NowTitle>{t("card.exposure")}</NowTitle>
      </NowTop>
      <SplitBar>
        <div style={{ width: `${long}%`, background: tokens.up }} />
        <div style={{ width: `${short}%`, background: tokens.down }} />
      </SplitBar>
      <NowEnds>
        <b>
          {t("labels.long")} {long}%
        </b>
        <b>
          {t("labels.short")} {short}%
        </b>
      </NowEnds>
      <NowMeta pin={true} items={[lastRebalance]} />
    </NowSection>
  );
};

/** A fact tile: a label over a value. */
const Fact = ({ label, value }: { label: ReactNode; value: ReactNode }) => (
  <div>
    {label}
    <b>{value}</b>
  </div>
);

/**
 * A bot with nothing traded yet: a pulse icon beside what it waits for. A
 * signals bot also shows how often its channel posts and when it last did
 * (the same query the new-bot introduction modal uses; only run for these
 * bots) in two tiles below.
 */
const NewBotPanel = ({ botData }: { botData: BasicBotData }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const isSignals = botData.bot_type === BotType.SIGNAL;
  const isStopped = botData.status !== BotStatus.ACTIVE;
  const { data } = sharedLib.sharedApi.bots.useGetSubGroupSignalFrequencyQuery(
    isSignals ? { bot_id: botData.id } : skipToken
  );
  const group = botData.sub_group_name;

  const facts: ReactNode[] = [];
  if (isSignals && data?.signals_frequency) {
    facts.push(
      <Fact
        key="frequency"
        label={t("card.channel_posts")}
        value={t("card.channel_posts_every", {
          time: getFormattedTimeDuration(data.signals_frequency),
        })}
      />
    );
  }
  if (isSignals && data?.last_signal_open_time) {
    facts.push(
      <Fact
        key="last"
        label={t("card.last_signal")}
        value={formatDateRelative(data.last_signal_open_time, true, true, true)}
      />
    );
  }

  const title = isStopped
    ? t("card.no_trades_yet")
    : isSignals
    ? t("card.waiting_first_signal")
    : t("card.waiting_first_alert");
  const description = isStopped
    ? undefined
    : isSignals
    ? group
      ? t("card.waiting_first_signal_desc", { group })
      : t("card.waiting_first_signal_generic_desc")
    : t("card.waiting_first_alert_desc");

  return (
    <NowSection data-now-state="idle">
      <Waiting>
        <Pulse aria-hidden={true}>
          <i />
          <i />
          <i />
          <i />
        </Pulse>
        <div>
          <WaitTitle>{title}</WaitTitle>
          {description && <WaitDescription>{description}</WaitDescription>}
        </div>
      </Waiting>
      {facts.length > 0 && <FactTiles $count={facts.length}>{facts}</FactTiles>}
    </NowSection>
  );
};

const LadderGap = styled.div`
  margin-top: 8px;
`;
const Bar = styled.div`
  position: relative;
  height: 8px;
  /* Room for the price pill above the bar, clear of the header row. */
  margin-top: 40px;
  border-radius: 4px;
`;
const Tick = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(255, 255, 255, 0.7);
`;
const Pin = styled.span`
  position: absolute;
  top: -4px;
  width: 2px;
  height: 16px;
  border-radius: 1px;
  transform: translateX(-50%);
`;
const PricePill = styled.span`
  position: absolute;
  top: -26px;
  transform: translateX(-50%);
  padding: 2px 6px;
  border-radius: 5px;
  font-size: 11.5px;
  line-height: 16px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${(p) => p.theme.colors.cardBgColor};
`;
const SplitBar = styled.div`
  display: flex;
  height: 8px;
  margin-top: 16px;
  border-radius: 4px;
  overflow: hidden;
  > div {
    min-width: 0;
  }
`;
const Waiting = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`;
const ripple = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  100% {
    transform: scale(1.6);
    opacity: 0;
  }
`;
/** Concentric blue circles; the outer ring ripples out softly. */
const Pulse = styled.span`
  position: relative;
  flex: none;
  width: 36px;
  height: 36px;
  i {
    position: absolute;
    border-radius: 50%;
    background: ${(p) => p.theme.colors.primaryBlue};
  }
  i:nth-child(1) {
    inset: 0;
    opacity: 0.12;
  }
  i:nth-child(2) {
    inset: 9px;
    opacity: 0.25;
  }
  i:nth-child(3) {
    inset: 14px;
  }
  /* The ripple: the outer ring scales up and fades on a slow loop. */
  i:nth-child(4) {
    inset: 0;
    opacity: 0;
    animation: ${ripple} 2.2s ease-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    i:nth-child(4) {
      animation: none;
      display: none;
    }
  }
`;
const WaitTitle = styled.div`
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const WaitDescription = styled.div`
  margin-top: 2px;
  font-size: 13px;
  line-height: 19px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const FactTiles = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$count}, minmax(0, 1fr));
  gap: 10px;
  margin-top: 16px;
  > div {
    min-width: 0;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => getNewLookTokens(p.theme).secondary};
    background: ${(p) =>
      p.theme.isDarkMode
        ? p.theme.colors.lightBlue
        : hexToRGB(p.theme.colors.lightBlue, 0.6)};
  }
  b {
    display: block;
    margin-top: 2px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
`;
